import {
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  FormGroup,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { InfoOutlined } from "@mui/icons-material";
import { CustomCheckbox } from "../StyledCheckbox";
import styles from "./ExportRecords.module.scss";
import CustomModal from "./CustomModal";

interface ExportRecordProps {
  open: boolean;
  requiredColumns: { headerName: string; field: string }[];
  closeModal: () => void;
  downloadCSV: () => void;
  downloadPDF: (selectedHeaders: string[]) => void;
}

/**
 * `ExportRecord` is a modal component that allows users to export records
 * in either CSV or PDF format.
 *
 * - When CSV is selected, the export is triggered immediately with all data.
 * - When PDF is selected, the user must select up to 6 columns (due to layout constraints).
 * - The modal validates the number of selected columns and displays an error if exceeded.
 *
 * ### Features:
 * - Radio toggle between CSV and PDF export formats.
 * - Checkbox list to select PDF columns (max 6).
 * - Reusable cancel and confirm modal controls.
 *
 * @component
 * @example
 * ```tsx
 * <ExportRecord
 *   open={isExportOpen}
 *   closeModal={() => setExportOpen(false)}
 *   requiredColumns={[{ headerName: 'Name', field: 'name' }, { headerName: 'Age', field: 'age' }]}
 *   downloadCSV={() => exportToCSV()}
 *   downloadPDF={(selected) => exportToPDF(selected)}
 * />
 * ```
 */
const ExportRecord: React.FC<ExportRecordProps> = ({
  open,
  closeModal,
  downloadCSV,
  downloadPDF,
  requiredColumns,
}) => {
  const [exportType, setExportType] = useState("csv");
  const [selectedHeaders, setSelectedHeaders] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  const handleExportConfirm = () => {
    if (exportType === "csv") {
      downloadCSV();
    } else {
      downloadPDF(selectedHeaders);
    }
    setSelectedHeaders([]);
    setExportType("csv");
    setErrorMsg("");
  };

  const handleCheckboxChange = (headerName: string) => {
    const alreadySelected = selectedHeaders.includes(headerName);

    if (alreadySelected) {
      setSelectedHeaders((prev) => prev.filter((h) => h !== headerName));
      setErrorMsg("");
    } else {
      if (selectedHeaders.length >= 6) {
        setErrorMsg("You can select a maximum of 6 columns.");
      } else {
        setSelectedHeaders((prev) => [...prev, headerName]);
        setErrorMsg("");
      }
    }
  };

  const toggleExportType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMsg("");
    setSelectedHeaders([]);
    setExportType(event.target.value);
  };

  useEffect(() => {
    if (!open) {
      setExportType("csv");
      setErrorMsg("");
      setSelectedHeaders([]);
    }
  }, [open]);

  const isExportDisabled = exportType === "pdf" && selectedHeaders.length === 0;

  return (
    <CustomModal
      title="Select Export Format"
      open={open}
      closeModal={closeModal}
      cancelButton={{
        label: "Cancel",
        type: "outlined",
        onClick: closeModal,
      }}
      confirmButton={{
        label: "Export",
        onClick: handleExportConfirm,
        disabled: isExportDisabled,
      }}
    >
      {() => (
        <Box className={styles.exportRecordContainer}>
          <RadioGroup
            value={exportType}
            onChange={toggleExportType}
            className={styles.radioGroup}
          >
            <FormControlLabel
              value="csv"
              control={<Radio />}
              label="Export via CSV"
              className={styles.radio}
            />
            <FormControlLabel
              value="pdf"
              control={<Radio />}
              label="Export via PDF"
              className={styles.radio}
            />
          </RadioGroup>
          {exportType === "pdf" && (
            <>
              <Typography variant="subtitle1">
                Select up to 6 columns to export:
                <Tooltip
                  title="Maximum of 6 columns can be selected due to PDF layout restrictions"
                  placement="right"
                >
                  <InfoOutlined className={styles.info} />
                </Tooltip>
              </Typography>
              <Box className={styles.pdfColumnsContainer}>
                <FormGroup>
                  {requiredColumns.map((col) => (
                    <FormControlLabel
                      key={col.field}
                      control={
                        <CustomCheckbox
                          checked={selectedHeaders.includes(col.headerName)}
                          onChange={() => handleCheckboxChange(col.headerName)}
                        />
                      }
                      label={col.headerName}
                    />
                  ))}
                </FormGroup>
              </Box>
            </>
          )}
          <div
            className={`${styles.actionBtns} ${
              exportType === "pdf" ? styles.pdfLayout : styles.csvLayout
            }`}
          >
            {exportType === "pdf" && errorMsg && (
              <div className={styles.pdfErrorMsg}>
                <Typography color="error" variant="body2">
                  {errorMsg}
                </Typography>
              </div>
            )}
          </div>
        </Box>
      )}
    </CustomModal>
  );
};

export default ExportRecord;
